import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FiLogOut, FiGrid, FiBookOpen, FiFileText, FiUsers, FiTrendingUp, FiEye, FiCalendar, FiClock, FiStar, FiSettings } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { getDashboardStats } from '../../services/api';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
`;

const Header = styled.header`
  background: white;
  padding: 1rem 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  h1 {
    color: ${props => props.theme.colors.primary};
    margin: 0;
    font-size: 1.8rem;
    font-weight: 700;
  }
  
  .badge {
    background: ${props => props.theme.colors.primary};
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: ${props => props.theme.colors.background};
  border-radius: 25px;
  border: 1px solid ${props => props.theme.colors.border};
`;

const UserAvatar = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  
  .name {
    font-weight: 600;
    color: ${props => props.theme.colors.text};
    font-size: 0.9rem;
  }
  
  .role {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 0.8rem;
  }
`;

const LogoutButton = styled.button`
  background: ${props => props.theme.colors.secondary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.secondaryDark};
    transform: translateY(-1px);
  }
`;

const Main = styled.main`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const WelcomeSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 200px;
    background: linear-gradient(135deg, ${props => props.theme.colors.primary}20, ${props => props.theme.colors.secondary}20);
    border-radius: 50%;
    transform: translate(50%, -50%);
  }
`;

const WelcomeTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem 0;
  font-size: 2.2rem;
  font-weight: 700;
`;

const WelcomeText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0;
  max-width: 600px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.color || props.theme.colors.primary};
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
`;

const StatIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: white;
  background: ${props => props.color || props.theme.colors.primary};
`;

const StatTrend = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.9rem;
  color: ${props => props.isPositive ? '#4CAF50' : '#f44336'};
  font-weight: 600;
`;

const StatTitle = styled.h3`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  color: ${props => props.theme.colors.text};
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
`;

const StatSubtitle = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin: 0.5rem 0 0 0;
  font-size: 0.9rem;
`;

const NavigationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const NavCard = styled(Link)`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    color: inherit;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.color || props.theme.colors.primary};
  }
`;

const NavIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  color: white;
  margin-bottom: 1.5rem;
  background: ${props => props.color || props.theme.colors.primary};
`;

const NavTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: 700;
`;

const NavDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
  font-size: 1rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const AdminDashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const data = await getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Set fallback data
      setStats({
        overview: {
          totalCourses: 0,
          totalCategories: 0,
          totalBlogs: 0,
          publishedBlogs: 0,
          totalViews: 0,
          monthlyGrowth: 0
        },
        recentActivity: { courses: [], blogs: [], categories: [] },
        trends: { coursesGrowth: 0, blogsGrowth: 0, categoriesGrowth: 0, viewsGrowth: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'A';
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Logo>
            <h1>RPL Support Admin</h1>
            <span className="badge">Dashboard</span>
          </Logo>
        </Header>
        <Main>
          <LoadingContainer>Loading dashboard...</LoadingContainer>
        </Main>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Logo>
          <h1>RPL Support Admin</h1>
          <span className="badge">Dashboard</span>
        </Logo>
        <UserInfo>
          <UserProfile>
            <UserAvatar>
              {getInitials(user?.firstName + ' ' + user?.lastName)}
            </UserAvatar>
            <UserDetails>
              <div className="name">{user?.firstName} {user?.lastName}</div>
              <div className="role">{user?.role}</div>
            </UserDetails>
          </UserProfile>
          <LogoutButton onClick={handleLogout}>
            <FiLogOut />
            Logout
          </LogoutButton>
        </UserInfo>
      </Header>

      <Main>
        <WelcomeSection>
          <WelcomeTitle>Welcome back, {user?.firstName}!</WelcomeTitle>
          <WelcomeText>
            Here's what's happening with your RPL Support platform today. 
            Manage your courses, blogs, and categories from this central dashboard.
          </WelcomeText>
        </WelcomeSection>

        <StatsGrid>
          <StatCard color="#4CAF50">
            <StatHeader>
              <StatIcon color="#4CAF50">
                <FiBookOpen />
              </StatIcon>
              <StatTrend isPositive={stats?.trends?.coursesGrowth > 0}>
                <FiTrendingUp />
                +{stats?.trends?.coursesGrowth || 0}%
              </StatTrend>
            </StatHeader>
            <StatTitle>Total Courses</StatTitle>
            <StatValue>{formatNumber(stats?.overview?.totalCourses || 0)}</StatValue>
            <StatSubtitle>RPL qualifications available</StatSubtitle>
          </StatCard>
          
          <StatCard color="#2196F3">
            <StatHeader>
              <StatIcon color="#2196F3">
                <FiFileText />
              </StatIcon>
              <StatTrend isPositive={stats?.trends?.blogsGrowth > 0}>
                <FiTrendingUp />
                +{stats?.trends?.blogsGrowth || 0}%
              </StatTrend>
            </StatHeader>
            <StatTitle>Blog Posts</StatTitle>
            <StatValue>{formatNumber(stats?.overview?.publishedBlogs || 0)}</StatValue>
            <StatSubtitle>Published articles</StatSubtitle>
          </StatCard>
          
          <StatCard color="#FF9800">
            <StatHeader>
              <StatIcon color="#FF9800">
                <FiGrid />
              </StatIcon>
              <StatTrend isPositive={stats?.trends?.categoriesGrowth > 0}>
                <FiTrendingUp />
                +{stats?.trends?.categoriesGrowth || 0}%
              </StatTrend>
            </StatHeader>
            <StatTitle>Categories</StatTitle>
            <StatValue>{formatNumber(stats?.overview?.totalCategories || 0)}</StatValue>
            <StatSubtitle>Industry sectors</StatSubtitle>
          </StatCard>
          
          <StatCard color="#9C27B0">
            <StatHeader>
              <StatIcon color="#9C27B0">
                <FiEye />
              </StatIcon>
              <StatTrend isPositive={stats?.trends?.viewsGrowth > 0}>
                <FiTrendingUp />
                +{stats?.trends?.viewsGrowth || 0}%
              </StatTrend>
            </StatHeader>
            <StatTitle>Total Views</StatTitle>
            <StatValue>{formatNumber(stats?.overview?.totalViews || 0)}</StatValue>
            <StatSubtitle>This month</StatSubtitle>
          </StatCard>
        </StatsGrid>

        <NavigationGrid>
          <NavCard to="/admin/courses" color="#4CAF50">
            <NavIcon color="#4CAF50">
              <FiBookOpen />
            </NavIcon>
            <NavTitle>Manage Courses</NavTitle>
            <NavDescription>
              Add, edit, and organize your RPL courses and qualifications. 
              Manage course details, requirements, and industry standards.
            </NavDescription>
          </NavCard>

          <NavCard to="/admin/categories" color="#2196F3">
            <NavIcon color="#2196F3">
              <FiGrid />
            </NavIcon>
            <NavTitle>Manage Categories</NavTitle>
            <NavDescription>
              Organize courses into categories and industry sectors. 
              Create and manage qualification levels and specializations.
            </NavDescription>
          </NavCard>

          <NavCard to="/admin/blogs" color="#FF9800">
            <NavIcon color="#FF9800">
              <FiFileText />
            </NavIcon>
            <NavTitle>Manage Blogs</NavTitle>
            <NavDescription>
              Create and edit blog posts with rich content and images. 
              Support for Markdown and HTML with SEO optimization.
            </NavDescription>
          </NavCard>

          <NavCard to="/admin/settings" color="#01675f">
            <NavIcon color="#01675f">
              <FiSettings />
            </NavIcon>
            <NavTitle>Site Settings</NavTitle>
            <NavDescription>
              Manage contact information, social media links, and other 
              website settings. Update email, phone, and business hours.
            </NavDescription>
          </NavCard>
        </NavigationGrid>
      </Main>
    </Container>
  );
};

export default AdminDashboardPage; 